<script setup lang="ts">
import {computed, onMounted, reactive, ref} from 'vue';

interface Stat {
    label: string;
    value: number;
    change: number;
    trend: 'up' | 'down' | 'neutral';
}

interface ChartItem {
    date: string;
    primary: number;
    secondary: number;
    tertiary: number;
}

interface Labels {
    primary: string;
    secondary: string;
    tertiary: string;
}

const show = reactive({
    primary: true,
    secondary: true,
    tertiary: true,
});

const hovered = reactive({
    index: null as number | null,
    data: null as ChartItem | null,
    x: ''
});

const onHover = (item: ChartItem, index: number, e: MouseEvent) => {
    hovered.index = index;
    hovered.data = item;
    hovered.x = index === 0 ?
        '3.5rem'
        :  (index + 1) === props.chart_data.length ?
            'calc(100% - 15rem)' :
        (index * section_width.value) + "%";
}

const onLeave = () => {
    hovered.index = null;
    hovered.data = null;
}

const props = withDefaults(defineProps<{
    period: string,
    stats: Stat[];
    chart_data: ChartItem[],
    labels: Labels
}>(), {
    period: 'Last 7 days',
    stats: () => [
        { label: 'Total Calls', value: 1247, change: 12.5, trend: 'up' },
        { label: 'Avg Duration', value: 324, change: -8.2, trend: 'down' },
        { label: 'Success Rate', value: 89, change: 2.3, trend: 'up' }
    ],
    chart_data: () => [
        { date: '2024-01-01', primary: 120, secondary: 85, tertiary: 45 },
        { date: '2024-01-02', primary: 135, secondary: 92, tertiary: 52 },
        { date: '2024-01-03', primary: 145, secondary: 88, tertiary: 48 },
        { date: '2024-01-04', primary: 130, secondary: 95, tertiary: 55 },
        { date: '2024-01-05', primary: 150, secondary: 100, tertiary: 60 },
        { date: '2024-01-06', primary: 140, secondary: 90, tertiary: 50 },
        { date: '2024-01-07', primary: 155, secondary: 105, tertiary: 65 },
        { date: '2024-01-08', primary: 160, secondary: 110, tertiary: 70 },
        { date: '2024-01-09', primary: 165, secondary: 115, tertiary: 75 },
        { date: '2024-01-10', primary: 170, secondary: 120, tertiary: 80 },
        { date: '2024-01-11', primary: 175, secondary: 125, tertiary: 85 },
        { date: '2024-01-12', primary: 180, secondary: 130, tertiary: 90 },
        { date: '2024-01-13', primary: 185, secondary: 135, tertiary: 95 },
        { date: '2024-01-14', primary: 190, secondary: 140, tertiary: 100 },
        { date: '2024-01-15', primary: 195, secondary: 145, tertiary: 105 },
        { date: '2024-01-16', primary: 200, secondary: 150, tertiary: 110 },
        { date: '2024-01-17', primary: 205, secondary: 155, tertiary: 115 },
        { date: '2024-01-18', primary: 210, secondary: 160, tertiary: 120 },
        { date: '2024-01-19', primary: 215, secondary: 165, tertiary: 125 },
        { date: '2024-01-20', primary: 220, secondary: 170, tertiary: 130 },
        { date: '2024-01-21', primary: 225, secondary: 175, tertiary: 135 },
        { date: '2024-01-22', primary: 230, secondary: 180, tertiary: 140 },
        { date: '2024-01-23', primary: 235, secondary: 185, tertiary: 145 },
        { date: '2024-01-24', primary: 240, secondary: 190, tertiary: 150 },
        { date: '2024-01-25', primary: 245, secondary: 195, tertiary: 155 },
        { date: '2024-01-26', primary: 250, secondary: 200, tertiary: 160 },
        { date: '2024-01-27', primary: 255, secondary: 205, tertiary: 165 },
        { date: '2024-01-28', primary: 260, secondary: 210, tertiary: 170 },
        { date: '2024-01-29', primary: 265, secondary: 215, tertiary: 175 },
        { date: '2024-01-30', primary: 270, secondary: 220, tertiary: 180 },
        { date: '2024-01-31', primary: 275, secondary: 225, tertiary: 185 },
    ],
    labels: () => ({
        primary: 'Calls',
        secondary: 'Answered',
        tertiary: 'Missed'
    })
});

const max_value = computed(() => {
    const allValues = props.chart_data.flatMap(item => [
        item.primary,
        item.secondary,
        item.tertiary
    ]);
    const max = Math.max(...allValues);

    // Round up to nearest 10 for cleaner scale
    return Math.ceil(max / 10) * 10;
    // return 60;
});

const section_width = computed(() => {
    return 100 / props.chart_data.length;
});

const sectionGap = (index: number) => section_width.value * index;

const barWidth = computed(() => {
    // Each bar takes up ~20% of the section, leaving room for gaps
    return section_width.value * 0.2;
});

const barGap = computed(() => {
    // Gap between bars as percentage
    return section_width.value * 0.05;
});

const groupStartOffset = computed(() => {
    // Total width: 3 bars + 2 gaps
    const totalGroupWidth = (barWidth.value * 3) + (barGap.value * 2);
    // Center the group within the section
    return (section_width.value - totalGroupWidth) / 2;
});

const getBarHeight = (value: number) => {
    if (max_value.value == 0) {
        return '0'
    }
    const perc = (value / max_value.value) * 100;
    return perc === 0 ? '0' : `${perc}%`
}

const getBarY = (value: number) => {
    if (max_value.value == 0) {
        return '0'
    }
    const perc = 100 - (value / max_value.value) * 100;

    return perc === 0 ? '0' : `${perc}%`
}

const getBarX = (index: number, spacer: number = 0) => {
    const perc = sectionGap(index) + groupStartOffset.value + (barWidth.value + barGap.value) * spacer;

    return perc === 0 ? '0' : `${perc}%`
}

const animateIn = ref(false);
onMounted(() => {
    setTimeout(() => {
        animateIn.value = true;
    }, 500);
});
</script>

<template>
    <div class="stats-graph">
        <div class="stats-graph-header">
            <div class="stats-graph-header-item" v-for="stat in stats" :key="stat.label">
                <div class="stats-graph-header-item-label">
                    <p>{{ stat.label }}</p>
                    <p>{{ stat.value }}</p>
                </div>

                <div class="stats-graph-header-item-compare">
                    <div class="stats-graph-header-item-compare-change">
                        <ah-icon :color="stat.trend === 'up' ? 'var(--color-lime)' : 'var(--color-secondary-orange)'" :size="36" :name="stat.trend === 'up' ? 'arrow-up' : 'arrow-down'" /> <span>{{ stat.change }}%</span>
                    </div>
                    <p>vs. last month</p>
                </div>
            </div>
        </div>

        <div class="stats-graph-chart">

            <div class="stats-graph-period-data" v-if="hovered.data" :style="{ left: hovered.x }">
                <p class="stats-graph-period-data-date">
                    {{new Date(hovered.data.date).toLocaleDateString('en-US', { month: "short", day: '2-digit', year: 'numeric' })}}
                </p>

                <div class="stats-graph-period-data-item" v-if="show.primary">
                    <span class="stats-graph-period-data-item-circle --primary"></span>
                    <span class="stats-graph-period-data-item-label">{{ labels.primary }}</span>
                    <span class="stats-graph-period-data-item-value">{{hovered.data.primary}}</span>
                </div>

                <div class="stats-graph-period-data-item" v-if="show.secondary">
                    <span class="stats-graph-period-data-item-circle --secondary"></span>
                    <span class="stats-graph-period-data-item-label">{{ labels.secondary }}</span>
                    <span class="stats-graph-period-data-item-value">{{hovered.data.secondary}}</span>
                </div>

                <div class="stats-graph-period-data-item" v-if="show.tertiary">
                    <span class="stats-graph-period-data-item-circle --tertiary"></span>
                    <span class="stats-graph-period-data-item-label">{{ labels.tertiary }}</span>
                    <span class="stats-graph-period-data-item-value">{{hovered.data.tertiary}}</span>
                </div>

            </div>

            <div class="stats-graph-chart-guide-vertical">
                <span>0</span>
                <div class="stats-graph-chart-guide-vertical-item" v-for="value in 6" :key="value">
                    {{ Math.floor(value * (max_value / 6)) }}
                </div>
            </div>

            <div class="stats-graph-chart-guide-horizontal">
                <div class="stats-graph-chart-guide-horizontal-item" v-for="value in chart_data" :key="value.date">
                    {{ new Date(value.date).toLocaleDateString('en-US', { day: '2-digit' }) }}
                </div>
            </div>

            <svg>
                <g v-for="(item, index) in chart_data" class="chart-section" @mouseenter.prevent="onHover(item, index, $event)" @mouseleave.prevent="onLeave" >

                    <rect
                        :x="`${sectionGap(index) == 0 ? '0' : sectionGap(index) + '%' }`"
                        y="0"
                        :width="`${section_width == 0 ? '0' : section_width + '%'}`"
                        height="100%" />

                    <g>
                        <rect
                            v-if="show.primary"
                            class="bar-rect --primary"
                            :x="`${getBarX(index)}`"
                            :y="animateIn ? `${getBarY(item.primary)}` : '100%'"
                            :width="`${barWidth}%`"
                            :height="animateIn ? getBarHeight(item.primary) : '0'"
                            fill="var(--color-hazy)">
                        </rect>

                        <rect
                            v-if="show.secondary"
                            class="bar-rect --secondary"
                            :x="`${getBarX(index,1)}`"
                            :y="animateIn ? `${getBarY(item.secondary)}` : '100%'"
                            :width="`${barWidth}%`"
                            :height="animateIn ? getBarHeight(item.secondary) : '0'"
                            fill="var(--color-tiffany-dark)">
                        </rect>

                        <rect
                            v-if="show.tertiary"
                            class="bar-rect --tertiary"
                            :x="`${getBarX(index,2)}`"
                            :y="animateIn ? `${getBarY(item.tertiary)}` : '100%'"
                            :width="`${barWidth}%`"
                            :height="animateIn ? getBarHeight(item.tertiary) : '0'"
                            fill="var(--color-orange)">
                        </rect>
                    </g>


                    <line :x1="`${sectionGap(index)}%`" y1="0" :x2="`${sectionGap(index)}%`" y2="100%" stroke-width=".1rem" stroke-dasharray="5,5"  />
                </g>

                <line :x1="0" :y1="`${100 - (value * 16.7)}%`" x2="100%" :y2="`${100 - (value * 16.7)}%`" stroke="var(--color-grey-dew)" stroke-width=".1rem" stroke-dasharray="5,5" v-for="value in 6" />
            </svg>
        </div>
        <div class="stats-graph-legend">
            <p class="stats-graph-legend-heading">{{period}}</p>

            <div class="stats-graph-legend-keys">
                <label class="stats-graph-legend-keys-item" v-for="(label, key) in labels" :key>
                    <ah-icon :name="show[key] ? 'checkbox-selected' : 'checkbox-deselected'" color="var(--color-hazy"/>
                    <input type="checkbox" v-model="show[key]">
                    <span>{{ label }}</span>
                    <span :class="['stats-graph-legend-keys-item-circle', '--' + key ]"></span>
                </label>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.stats-graph {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    &-period-data {
        position: absolute;
        top: 20%;
        z-index: 999999999999;
        width: 15rem;
        background: var(--color-white-60);
        -webkit-backdrop-filter: blur(1.2rem);
        backdrop-filter: blur(1.2rem);
        border-radius: 2.4rem;
        padding: 2rem;
        box-shadow: 0 8px 16px 0 rgba(0,0,0,0.10);
        display: flex;
        flex-direction: column;
        gap: 1rem;
        pointer-events: none;

        &-date {
            @include font(1.4, 1.4, black);
        }

        &-item {
            display: flex;
            gap: 1rem;
            align-items: center;

            &-circle {
                width: 1.2rem;
                height: 1.2rem;
                border-radius: 50%;
                background: red;

                &.--primary {
                    background: var(--color-hazy);
                }
                &.--secondary {
                    background: var(--color-tiffany-dark);
                }
                &.--tertiary {
                    background: var(--color-orange);
                }
            }
            &-label, &-value {
                @include font(1,1,medium);
            }

            &-label {
                flex: 1;
            }
        }

        @include dark() {
            background: rgba(42, 57, 66, .6);

            &-item {
                &-label, &-value {
                    color: var(--color-mist);
                }
            }
        }
    }

    &-header {
        display: flex;
        gap: 2rem;
        flex-wrap: wrap;

        &-item {
            flex: 1;
            height: 11.4rem;
            background: var(--color-white);
            border-radius: 7rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 2rem;
            padding: 1rem 1rem 1rem 4rem;

            &-label {
                display: flex;
                flex-direction: column;
                gap: 1.5rem;

                p {
                    @include font(1.4, 1.8, black);

                    &:last-child {
                        @include font(4.6, 4.6, book);
                    }
                }
            }

            &-compare {
                width: 20rem;
                height: 9.4rem;
                background: var(--color-grey-mist);
                border-radius: 6rem;
                padding: 2rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: .6rem;

                &-change {
                    display: flex;
                    align-items: center;

                    .icon {
                        width: 3.6rem;
                        height: 3.6rem;
                    }

                    span {
                        @include font(3.6, 3.6, book);
                    }
                }

                p {
                    @include font(1.2, 1.2, black);
                    color: var(--color-hazy);
                }
            }

            @include dark() {
                background: var(--color-darth-maul);

                &-label {
                    p {
                        color: var(--color-mist);
                    }
                }

                &-compare {
                    background: var(--color-darth-sidious);

                    &-change {
                        span {
                            color: var(--color-mist);
                        }
                    }
                }
            }
        }
    }

    &-chart {
        padding: 0 0 3.5rem 3.5rem;
        position: relative;

        svg {
            width: 100%;
            height: 49rem;
            border: .1rem dashed var(--color-grey-dew);

            line {
                stroke: var(--color-grey-dew);
            }

            .chart-section {
                fill: var(--color-white);
                transition: .3s ease;
                border: dashed var(--color-grey-dew);
                border-width: 0 .1rem;

                &:hover {
                    fill: rgba(0, 154, 145, .15)!important;
                }

                &:nth-child(even) {
                    fill: var(--color-grey-mist);
                }

            }

            .bar-rect {
                transition: height .4s ease, y .4s ease;
            }

            @include dark() {
                border: 0.1rem dashed var(--color-darth-maul);

                line {
                    stroke: var(--color-darth-maul);
                }

                .chart-section {
                    fill: var(--color-darth-vader);

                    &:nth-child(even) {
                        fill: var(--color-white-05);
                    }

                    &:hover {
                        fill: rgba(0, 154, 145, .4)!important;
                    }
                }
            }
        }

        &-guide {
            &-vertical {
                width: 3.5rem;
                height: calc(100% - 4.4rem);
                position: absolute;
                top: 0;
                left: 0;
                display: flex;
                flex-direction: column-reverse;

                &-item {
                    flex: 1;
                    border-width: .1rem 0;
                    margin-top: -1rem;
                    @include font(1, -.5, black);
                    color: var(--color-hazy);
                }

                span {
                    position: absolute;
                    bottom: -1rem;
                    left: 0;
                    @include font(1, -.5, black);
                    color: var(--color-hazy);
                }
            }

            &-horizontal {
                width: calc(100% - 3.5rem);
                height: 3.5rem;
                position: absolute;
                bottom: 0;
                right: 0;
                display: flex;
                justify-content: space-between;
                align-items: center;

                &-item {
                    @include font(1, -.5, black);
                    color: var(--color-hazy);
                    flex: 1;
                    text-align: center;
                }
            }
        }

    }

    &-legend {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2rem;

        &-heading {
            @include font(1.4, 1.8, black);
        }

        &-keys {
            display: flex;
            gap: 4rem;

            &-item {
                display: flex;
                gap: 1rem;
                align-items: center;

                input {
                    display: none;
                }

                &-circle {
                    width: 1.2rem;
                    height: 1.2rem;
                    border-radius: 50%;

                    &.--primary {
                        background: var(--color-hazy);
                    }
                    &.--secondary {
                        background: var(--color-tiffany-dark);
                    }
                    &.--tertiary {
                        background: var(--color-orange);
                    }
                }

                span {
                    @include font(1,1,medium);
                }

                @include dark() {
                    color: var(--color-mist);
                }
            }
        }
    }
}
</style>
